// Patient scheduling page — ENGLISH locale (2026-06, the i18n commit).
//
// THE SINGLE SOURCE for every user-facing word on the patient page. No
// component in this flow may hold a user-facing literal (pinned at grep
// level by backend/tests/test_patient_scheduling_i18n.py).
//
// KEYS ARE SEMANTIC, never English-strings-as-keys: a copy edit never
// renames a key, and the two locale files stay readable as documents for
// the native-speaker review gate.
//
// NOT TRANSLATED, deliberately (the exclusion list): clinic names and
// cities (roster values, rendered as supplied), the WestStar logo and its
// alt text (the brand the patient knows), "Powered by Manna AI Solutions"
// (the vendor mark), the phone number, and the "~N mi" distance label the
// server composes (es-US navigation renders "mi" identically).

const en = {
  language: {
    title: "Choose your language",
    body: "Your scheduling page will be in the language you pick. You can change it at any time.",
    english: "English",
    spanish: "Español",
  },
  header: {
    switchLabel: "Language",
    english: "EN",
    spanish: "ES",
    englishFull: "English",
    spanishFull: "Español",
  },
  common: {
    continue: "Continue",
    timezone: "Pacific",
  },
  loading: {
    text: "Loading…",
  },
  unavailable: {
    title: "This link isn't available",
    body: "If you already booked a visit or sent us your times, you're all set: your text messages have the details. If not, give us a call and we'll get you scheduled.",
  },
  done: {
    title: "Got it",
    body: "WestStar will text you to confirm one of your times. Nothing is booked yet — the text is how you'll know.",
    changeNote: "Need to change your times? Call your clinic.",
    questions: "Questions? Call {phone}.",
  },
  clinic: {
    title: "Where would you like to go?",
    body: "These are the WestStar locations that can see you. Your clinic is selected — tap another if you'd rather go there.",
    yours: "Your clinic",
    changeTimes: "Change my times",
  },
  review: {
    title: "Look right?",
    locationLabel: "Location",
    changeLocation: "Change location",
    changeTimes: "Change my times",
    notBookingNote: "Your clinic will confirm one of these times by text — sending availability is not booking an appointment.",
    send: "Send my availability",
    sending: "Sending…",
  },
  // Telehealth checkbox (2026-10-07): Geoff's wording, verbatim (curly
  // apostrophes as he wrote it). Shown on the review step when the chosen
  // clinic offers it; checked by default.
  telehealth: {
    label: "Yes, I’d like the option to start with a telehealth evaluation if an in-person appointment isn’t available within 3 business days.",
    note: "You can continue with telehealth visits if you prefer, or transition to in-person physical therapy when available.",
  },
  flow: {
    titleNamed: "Hi {name}, when are you free?",
    title: "When are you free?",
    intro: "Tap the times that work for your first visit at {clinic}. Pick up to {max}. All times are {timezone}.",
    clinicFallback: "WestStar Physical Therapy",
    laterDates: "Later dates",
    counter: "{count} of {max} times chosen",
    exactOpen: "Give exact hours instead",
    from: "From",
    until: "Until",
    exactAdd: "Add these hours",
  },
  chips: {
    morning: "Morning",
    afternoon: "Afternoon",
    late_afternoon: "Late afternoon",
    // COPY, not a formatted datum — deliberately the coarse spoken form
    // ("12 – 3 PM", not "12:00 PM – 3:00 PM"), which is why these three
    // are locale strings while every real date/time on the page goes
    // through Intl. The windows themselves are the pinned constants in
    // the page's CHIPS array; these only describe them.
    morningHint: "8 AM – 12 PM",
    afternoonHint: "12 – 3 PM",
    late_afternoonHint: "3 – 6 PM",
  },
  status: {
    added: "{window} added. {count} of {max} times chosen.",
    removed: "{window} removed. {count} of {max} times chosen.",
    exactAdded: "Exact hours added. {count} of {max} times chosen.",
    clinicSelected: "{clinic} selected.",
    languageSet: "The page is now in English.",
  },
  errors: {
    minWindow: "Please give us at least a {minutes}-minute window.",
    endAfterStart: "The until time needs to be after the from time.",
    maxWindows: "You can choose up to {max} times.",
    maxWindowsRemove: "You can choose up to {max} times. Remove one to add another.",
    sendFailed: "Something went wrong sending your times. Please try again.",
    // The fallback for a server reason code this page does not know: never
    // the raw code, never English inside a Spanish flow.
    unknown: "Something went wrong. Please call us at {phone}.",
  },
  // Wave 3 (2026-10-06): booking a real open time at a WestStar clinic. Shown
  // only when the server says self_book: true for the chosen clinic; every
  // other patient keeps the pick-your-windows flow above.
  book: {
    checking: "Finding open times…",
    title: "Pick a time",
    intro: "These are the open times for a first visit at {clinic}. Choose a day, then a time.",
    changeClinic: "Choose a different clinic",
    dayCount: "{count} open",
    filterAll: "All times",
    filterAm: "Before noon",
    filterPm: "After noon",
    noTimesDay: "No open times in this part of the day. Try another day or time of day.",
    noTimes: "There are no open times in these dates.",
    laterDates: "See later dates",
    earlierDates: "Back to earlier dates",
    noneWork: "None of these times work for me",
    noneWorkHint: "Tell us when you're free and we'll call you to schedule.",
    confirmTitle: "Book this time?",
    confirmBtn: "Book this time",
    booking: "Booking…",
    otherTime: "Choose a different time",
    bookedTitle: "You're booked",
    bookedBody: "Your first visit is booked. We'll send you a text to confirm it.",
    bookedChange: "Need to change it? Call us at {phone}.",
    taken: "Someone just booked that time. Please choose another one.",
    failed: "We couldn't book that time just now. Choose another time, or call us at {phone}.",
    dayChosen: "{day}: {count} open times",
  },
  // The server's own prose, mapped to keys. The page renders THESE, never
  // the strings the API returns — the API bodies stay EN and byte-identical
  // so the no-oracle and PHI-minimal properties of the public surface are
  // untouched (see the receipts' §0.1 asymmetry note).
  backendMessages: {
    linkUnavailable: "This scheduling link is no longer available. If you already booked a visit or sent us your times, you're all set. If not, call us at {phone} and we'll get you scheduled.",
    rateLimited: "Too many attempts. Please wait a minute and try your link again, or call us at {phone}.",
    clinicRefused: "Please choose one of the clinics shown.",
    unsupportedLanguage: "That language isn't available yet.",
    windowsNotList: "Please choose at least one time.",
    chooseAtLeastOne: "Please choose at least one time.",
    chooseAtMost: "Please choose at most {max} times.",
    windowNeedsFields: "Each time needs a date, a start and an end.",
    datePassed: "That date has already passed.",
    dateTooFar: "That date is too far out.",
    endBeforeStart: "The end time must be after the start time.",
    windowTooShort: "Each time needs at least {minutes} minutes.",
  },
};

export default en;
